// Maestro de Pañoles — alta, listado, edición y link a ficha de cada uno.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Package, Plus, Truck, Factory, ArrowRight, Loader2, Edit, AlertCircle, User, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';

const TIPOS = [
  { val: 'matriz', label: 'Matriz', icon: Factory, color: 'bg-indigo-100 text-indigo-800' },
  { val: 'faena', label: 'Faena', icon: Package, color: 'bg-emerald-100 text-emerald-800' },
  { val: 'movil', label: 'Móvil', icon: Truck, color: 'bg-amber-100 text-amber-800' },
];

export default function PanolesPage() {
  const navigate = useNavigate();
  const [panoles, setPanoles] = useState([]);
  const [sinPanol, setSinPanol] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: '', tipo: 'faena', ubicacion_geo: '', ot_asociada: '',
    responsable_nombre: '', responsable_rut: '', responsable_tipo: 'supervisor',
  });
  const [saving, setSaving] = useState(false);

  // Buscar responsable
  const [respSearch, setRespSearch] = useState('');
  const [respResults, setRespResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bodega/panoles?activos_solo=true');
      setPanoles(res.data?.items || []);
      setSinPanol(res.data?.sin_panol_count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando pañoles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Carlos (10/05/2026): imprimir y exportar Pañoles
  const imprimirPanoles = () => {
    if (!panoles.length) { toast.warning('Sin pañoles para imprimir'); return; }
    printTable({
      title: 'Maestro de Pañoles',
      subtitle: `${panoles.length} pañoles · Generado ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total pañoles', value: panoles.length },
        { label: 'Matriz', value: panoles.filter(p => p.tipo === 'matriz').length },
        { label: 'Faena', value: panoles.filter(p => p.tipo === 'faena').length },
        { label: 'Móvil', value: panoles.filter(p => p.tipo === 'movil').length },
      ],
      columns: [
        { key: 'codigo', label: 'Código', mono: true, align: 'center' },
        { key: 'nombre', label: 'Nombre' },
        { key: 'tipo', label: 'Tipo', align: 'center', format: (v) => (v || '').toUpperCase() },
        { key: 'ubicacion_geo', label: 'Ubicación' },
        { key: 'ot_asociada', label: 'OT', align: 'center' },
        { key: 'responsable_nombre', label: 'Responsable' },
        { key: 'responsable_tipo', label: 'Cargo' },
        { key: 'total_herramientas', label: 'Herram.', align: 'right' },
      ],
      rows: panoles,
    });
  };

  const exportarPanolesExcel = () => {
    if (!panoles.length) { toast.warning('Sin pañoles para exportar'); return; }
    exportToCSV({
      filename: 'panoles',
      title: 'CHIRINO STEEL · Maestro de Pañoles',
      summary: [
        { label: 'Total pañoles', value: panoles.length },
        { label: 'Matriz', value: panoles.filter(p => p.tipo === 'matriz').length },
        { label: 'Faena', value: panoles.filter(p => p.tipo === 'faena').length },
        { label: 'Móvil', value: panoles.filter(p => p.tipo === 'movil').length },
        { label: 'Sin pañol', value: sinPanol },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'nombre', label: 'Nombre' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'ubicacion_geo', label: 'Ubicación geo' },
        { key: 'ot_asociada', label: 'OT asociada' },
        { key: 'responsable_nombre', label: 'Responsable' },
        { key: 'responsable_rut', label: 'RUT responsable' },
        { key: 'responsable_tipo', label: 'Cargo responsable' },
        { key: 'total_herramientas', label: 'Total herramientas', format: (v) => Number(v) || 0 },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: panoles,
    });
  };

  useEffect(() => {
    const t = setTimeout(async () => {
      if (respSearch.length >= 2) {
        const res = await api.get(`/bodega/buscar-trabajador?q=${encodeURIComponent(respSearch)}&solo_activos=true`);
        setRespResults(res.data?.trabajadores || []);
      } else setRespResults([]);
    }, 300);
    return () => clearTimeout(t);
  }, [respSearch]);

  const openNew = () => {
    setEditing(null);
    setForm({
      nombre: '', tipo: 'faena', ubicacion_geo: '', ot_asociada: '',
      responsable_nombre: '', responsable_rut: '', responsable_tipo: 'supervisor',
    });
    setRespSearch('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nombre: p.nombre || '',
      tipo: p.tipo || 'faena',
      ubicacion_geo: p.ubicacion_geo || '',
      ot_asociada: p.ot_asociada || '',
      responsable_nombre: p.responsable_nombre || '',
      responsable_rut: p.responsable_rut || '',
      responsable_tipo: p.responsable_tipo || 'supervisor',
    });
    setRespSearch(p.responsable_nombre || '');
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ot_asociada: parseInt(form.ot_asociada) || 0,
      };
      if (editing) {
        await api.patch(`/bodega/panoles/${editing.id}`, payload);
        toast.success('Pañol actualizado');
      } else {
        await api.post('/bodega/panoles', payload);
        toast.success('Pañol creado');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-panoles">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-indigo-600" /> Pañoles
          </h1>
          <p className="text-sm text-muted-foreground">
            {panoles.length} pañoles activos · Matriz + Faenas + Móviles
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={imprimirPanoles} title="Imprimir listado" data-testid="btn-print-panoles">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" onClick={exportarPanolesExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-panoles">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" onClick={() => navigate('/bodega/panoles/bulk-responsables')} data-testid="btn-bulk-responsables">
            <User className="w-4 h-4 mr-1" /> Responsables bulk
          </Button>
          <Button onClick={openNew} data-testid="btn-nuevo-panol">
            <Plus className="w-4 h-4 mr-1" /> Nuevo Pañol
          </Button>
        </div>
      </div>

      {sinPanol > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span><strong>{sinPanol} herramientas</strong> en cargo sin pañol asignado — considerá reasignarlas a un pañol para mejor control.</span>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {panoles.map((p) => {
            const tipo = TIPOS.find(t => t.val === p.tipo) || TIPOS[1];
            const TipoIcon = tipo.icon;
            return (
              <Card
                key={p.id}
                className="hover:shadow-md transition cursor-pointer"
                onClick={() => navigate(`/bodega/panoles/${p.id}`)}
                data-testid={`card-panol-${p.id}`}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <TipoIcon className="w-4 h-4 text-slate-500" />
                        <Badge className={`text-[10px] h-4 ${tipo.color}`}>{tipo.label}</Badge>
                      </div>
                      <div className="font-semibold text-sm mt-1 truncate">{p.nombre}</div>
                      {p.ubicacion_geo && <div className="text-xs text-muted-foreground truncate">📍 {p.ubicacion_geo}</div>}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                      className="text-muted-foreground hover:text-foreground p-1"
                      data-testid={`btn-edit-${p.id}`}
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-xs space-y-0.5">
                    {p.responsable_nombre ? (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <User className="w-3 h-3" /> {p.responsable_nombre}
                        {p.responsable_tipo && <span className="text-[10px]">· {p.responsable_tipo}</span>}
                      </div>
                    ) : (
                      <div className="text-amber-600 italic">sin responsable asignado</div>
                    )}
                    {p.ot_asociada > 0 && <div className="text-muted-foreground">OT {p.ot_asociada}</div>}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-2xl font-bold text-indigo-700">{p.total_herramientas}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      herramientas <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-panol">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Pañol' : 'Nuevo Pañol'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Modificá los datos del pañol' : 'Crea un nuevo pañol para asignar herramientas'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium block mb-1">Nombre *</label>
              <Input
                value={form.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: PAÑOL CAMANCHACA CORONEL"
                data-testid="input-panol-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium block mb-1">Tipo</label>
                <select
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={form.tipo}
                  onChange={e => setForm({ ...form, tipo: e.target.value })}
                  data-testid="select-panol-tipo"
                >
                  {TIPOS.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">OT asociada</label>
                <Input
                  type="number"
                  value={form.ot_asociada}
                  onChange={e => setForm({ ...form, ot_asociada: e.target.value })}
                  placeholder="Opcional"
                  data-testid="input-panol-ot"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Ubicación geográfica</label>
              <Input
                value={form.ubicacion_geo}
                onChange={e => setForm({ ...form, ubicacion_geo: e.target.value })}
                placeholder="Ej: Coronel, Chile"
                data-testid="input-panol-geo"
              />
            </div>
            <div className="relative">
              <label className="text-xs font-medium block mb-1">Responsable</label>
              <Input
                value={respSearch}
                onChange={e => { setRespSearch(e.target.value); setForm({ ...form, responsable_nombre: '', responsable_rut: '' }); }}
                placeholder="Buscar nombre o RUT…"
                data-testid="input-panol-responsable"
              />
              {respResults.length > 0 && respSearch !== form.responsable_nombre && (
                <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {respResults.map((r, i) => (
                    <button key={i} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                      onClick={() => {
                        setForm({ ...form, responsable_nombre: r.nombre, responsable_rut: r.rut || '' });
                        setRespSearch(r.nombre);
                        setRespResults([]);
                      }}>
                      {r.nombre} <span className="text-muted-foreground text-xs">({r.rut || r.email || ''})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Tipo de responsable</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={form.responsable_tipo}
                onChange={e => setForm({ ...form, responsable_tipo: e.target.value })}
              >
                <option value="">Sin especificar</option>
                <option value="supervisor">Supervisor</option>
                <option value="panolero">Pañolero</option>
                <option value="chofer">Chofer</option>
                <option value="maestro">Maestro</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={submit} disabled={saving || !form.nombre.trim()} data-testid="btn-guardar-panol">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Guardar' : 'Crear Pañol'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
